import { FieldValidator } from 'final-form';
import { useCallback } from 'react';
import moment, { Moment } from 'moment';
import { Field } from 'react-final-form';
import { useTranslation } from 'react-i18next';

import { composeValidators } from '~src/shared/reactFinalForm/validators';
import { FieldType } from '~src/types';
import CustomDatePicker from '~src/shared/form/inputs/CustomDatePicker';
import { validatorRequired } from '~src/services/validators';
import RepeatSelector from '~src/modules/okrs/manageObjectives/shared/repetition/RepeatSelector';
import SvgIcon from '~src/shared/SvgIcon';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import { checkDateFormat } from '~src/utils';
import VisibilitySelector, { VisibilityObj } from '~src/modules/okrs/manageObjectives/shared/visibility/VisibilitySelector';
import { Repetition, SubInfoContainer } from '~src/modules/okrs/manageObjectives/shared/shared.sc';
import { DoubleDates } from '~src/shared/form/shared.sc';

// Due to this article: https://www.jondjones.com/frontend/react/npm-packages-for-react-developers/react-redux-form-error-using-validation-maximum-update-depth-exceeded/ I moved this function out of the component
const validateDates: FieldValidator<string> = (_: string, allValues: object | undefined) => {
  const values = allValues as { start_date: string | undefined; end_date: string | undefined };
  if (!values.start_date || !values.end_date) {
    return;
  }
  const startDate = moment.utc(values.start_date);
  const endDate = moment.utc(values.end_date);
  if (endDate.isBefore(startDate)) {
    return 'okr.due_date_before_start';
  }
};

const ObjectiveSubInfo = () => {
  const renderDate = useCallback(({ input, label, meta: { touched, error } }: FieldType) => {
    const onChangeDatePicker = (date: Moment | null) => {
      const newDate = date === null ? '' : moment(date).format('YYYY-MM-DD');
      input.onChange(newDate);
    };
    return (
      <CustomDatePicker
        error={touched ? error : null}
        dateValue={checkDateFormat(input.value)}
        label={label}
        labelKey={label}
        onChange={onChangeDatePicker}
      />
    );
  }, []);

  const renderRepeat = useCallback(({ input, label, meta: { touched, error } }: FieldType) => {
    const clear = () => input.onChange('');
    return (
      <Repetition>
        <RepeatSelector {...input} error={touched ? error : null} label={label} />
        {input.value && (
          <div onClick={clear}>
            <SvgIcon width={20} name="iconCross" />
          </div>
        )}
      </Repetition>
    );
  }, []);

  const renderDropdown = useCallback(({ input, label, meta: { touched, error } }: FieldType) => {
    const onChanging = (val: VisibilityObj) => input.onChange(val);
    return (
      <VisibilitySelector
        value={input.value}
        onChange={onChanging}
        label={label}
        error={touched ? error : null}
      />
    );
  }, []);

  const [t] = useTranslation();

  return (
    <SubInfoContainer>
      <SelectorHandler hoverTextKey="x.objective.add.icon.dates" iconName="iconCalenderTime">
        {(_, select, blur) => (
          <DoubleDates>
            <Field
              name="start_date"
              label={t('x.okr.objective.form.dates.start')}
              component={renderDate}
              validate={composeValidators([validatorRequired, validateDates])}
              onFocus={select}
              onBlur={blur}
            />
            <Field
              name="end_date"
              label={t('x.okr.objective.form.dates.due')}
              component={renderDate}
              validate={composeValidators([validatorRequired, validateDates])}
              onFocus={select}
              onBlur={blur}
            />
          </DoubleDates>
        )}
      </SelectorHandler>
      <SelectorHandler hoverTextKey="x.objective.add.icon.reminder" iconName="iconAlarmClock">
        {(_, select, blur) => (
          <Field
            name="repeat"
            label={t('x.okr.objective.form.reminder')}
            component={renderRepeat}
            onFocus={select}
            onBlur={blur}
          />
        )}
      </SelectorHandler>
      <SelectorHandler hoverTextKey="x.objective.add.icon.visibility" iconName="iconEye">
        {(_, select, blur) => (
          <Field
            name="visibility"
            label={t('x.okr.objective.form.visibility')}
            component={renderDropdown}
            validate={validatorRequired}
            onFocus={select}
            onBlur={blur}
          />
        )}
      </SelectorHandler>
    </SubInfoContainer>
  );
};

export default ObjectiveSubInfo;
